import React from 'react'
import GuideLayout from '@/components/help/GuideLayout'
import GuideSection from '@/components/help/GuideSection'
import GuideWidget from '@/components/help/GuideWidget'
import GuideTip from '@/components/help/GuideTip'
import GuideRelatedLinks from '@/components/help/GuideRelatedLinks'
import SensitivityExplorer from '@/components/help/widgets/SensitivityExplorer'
import EventDemandScenario from '@/components/help/widgets/EventDemandScenario'
import { usePage } from '@inertiajs/react'
import { FALLBACK_BRAND } from '@/lib/brand'
import type { SharedProps } from '@/types/shared'

export default function DemandResponse({ currentUser, properties }: Pick<SharedProps, 'currentUser' | 'properties'>) {
  const brandProp = usePage().props.brand
  const brand = typeof brandProp === 'string' && brandProp.trim() !== '' ? brandProp : FALLBACK_BRAND
  // Room-Type Demand is globally kill-switched (App::RateEngine::Config). When off, the guide
  // drops the Room Demand layer so it never describes a stage the engine no longer runs.
  const roomDemandEnabled = usePage().props.roomDemandEnabled === true

  return (
    <GuideLayout
      currentUser={currentUser}
      properties={properties}
      title="Demand Response"
      subtitle="How rates respond automatically to booking pace, occupancy, and market signals."
    >
      <GuideSection title="Why Dynamic Pricing?">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Static rules like seasons and day-of-week capture patterns you know in advance. But demand is
          unpredictable -- a surprise conference, an unexpectedly quiet week, a competitor closing for
          refurbishment. These are the things no calendar rule can anticipate.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Demand response is the engine's{' '}
          <strong className="text-foreground">intelligence layer</strong>. It watches your actual booking
          pace versus what is historically normal, and adjusts rates up or down accordingly.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Higher demand than expected → rates increase to capture the revenue opportunity. Lower demand →
          rates decrease to stimulate bookings before it is too late.
        </p>
      </GuideSection>

      <GuideSection title={roomDemandEnabled ? 'Two-Layer Demand Model' : 'Property Demand'}>
        {roomDemandEnabled ? (
          <>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Demand operates in <strong className="text-foreground">two layers</strong>, each at a
              different scope:
            </p>
            <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
              <li>
                <strong className="text-foreground">Property Demand (Stage 4)</strong> — looks at the
                overall booking pace across the entire property. Are bookings ahead or behind your
                historical baseline? This drives the primary demand adjustment for all room types.
              </li>
              <li>
                <strong className="text-foreground">Room Demand (Stage 5)</strong> — fine-tunes pricing
                for individual room types. If your Sea View Suite is filling faster than the property
                average, it gets an additional price lift. If your Standard Double is lagging, it gets
                a discount. This ensures each room type is priced according to its own demand. When a
                room has linked rooms, the engine treats them as one{' '}
                <strong className="text-foreground">room family</strong> and prices against the combined{' '}
                <strong className="text-foreground">family occupancy</strong> across all its units.
              </li>
            </ul>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Both layers include{' '}
              <strong className="text-foreground">lead time amplification</strong> — the engine reacts
              more strongly to demand signals as check-in approaches, and more gently for dates far in
              the future.
            </p>
          </>
        ) : (
          <>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Demand looks at the overall booking pace across the{' '}
              <strong className="text-foreground">entire property</strong>. Are bookings ahead or
              behind your historical baseline? This drives the demand adjustment applied to every
              room type.
            </p>
            <p className="text-sm leading-relaxed text-muted-foreground">
              It includes <strong className="text-foreground">lead time amplification</strong> — the
              engine reacts more strongly to demand signals as check-in approaches, and more gently
              for dates far in the future.
            </p>
          </>
        )}
      </GuideSection>

      <GuideSection title="How It Works: Booking Curve">
        <p className="text-sm leading-relaxed text-muted-foreground">
          The <strong className="text-foreground">booking curve</strong> is the primary mode. The
          engine compares your current bookings against a historical baseline for the same lead time, and
          words where you stand on a five-band scale — always paired with a plain read of the
          numbers, e.g. <em>&ldquo;32% full · usually ~61% by now&rdquo;</em>:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-emerald-600">Ahead</strong> or{' '}
            <strong className="text-emerald-600">Well ahead</strong> — you are further along than the
            baseline for this lead time (demand is strong) → rates nudge up.
          </li>
          <li>
            <strong className="text-foreground">On track</strong> — you are close to the baseline → no
            adjustment.
          </li>
          <li>
            <strong className="text-rose-600">Behind</strong> or{' '}
            <strong className="text-rose-600">Well behind</strong> — you are short of the baseline
            (demand is soft) → rates ease down to stimulate bookings.
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          The exact underlying number the engine works from — how your booking pace compares to the
          baseline as a raw ratio — is kept out of the way. Open{' '}
          <strong className="text-foreground">&ldquo;How this was calculated&rdquo;</strong> on any
          calendar day to see it, along with sample counts and confidence.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          The baseline builds automatically from your booking history. New properties need{' '}
          <strong className="text-foreground">2--4 weeks of data</strong> before this mode kicks in.
        </p>
      </GuideSection>

      <GuideSection title="Sensitivity Presets">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Three options control how aggressively the engine reacts to pace deviations:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Conservative</strong> -- gentle adjustments (-12% to +22%).
            Best for steady-demand properties or price-sensitive markets. Rates change slowly.
          </li>
          <li>
            <strong className="text-foreground">Balanced</strong> -- measured adjustments (-20% to +45%). The
            recommended starting point for most properties.
          </li>
          <li>
            <strong className="text-foreground">Aggressive</strong> -- sharp adjustments (-30% to +70%). Best
            for high-demand locations or properties with limited inventory. Rates move sharply.
          </li>
        </ul>

        <GuideWidget
          title="Sensitivity Explorer"
          footnote="Uses the same interpolation curves as the actual rate engine. Does not apply the demand cap — actual adjustments may be smaller at extreme scenarios."
        >
          <SensitivityExplorer />
        </GuideWidget>
      </GuideSection>

      <GuideSection title="Lead Time Amplification">
        <p className="text-sm leading-relaxed text-muted-foreground">
          The same demand signal means different things depending on when check-in is. Being 50%
          booked for a date 2 months away is fine -- there is plenty of time to sell remaining rooms.
          Being 50% booked for tomorrow is a problem.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Lead time amplification is <strong className="text-foreground">built into</strong> both
          demand stages. It automatically{' '}
          <strong className="text-foreground">amplifies</strong> demand adjustments as check-in gets
          closer, and <strong className="text-foreground">dampens</strong> them further out. This
          happens with sensible defaults — no configuration needed.
        </p>
        <div className="rounded-lg border bg-muted/50 p-4">
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Example:</strong> If balanced sensitivity says +12%
            for strong demand, that becomes +6% at 60 days out (stay calm) but +19% at 3 days out
            (act now).
          </p>
        </div>
        <p className="text-sm leading-relaxed text-muted-foreground">
          You can adjust the lead time curve and set a maximum demand adjustment in the{' '}
          <strong className="text-foreground">Advanced</strong> section of the Demand Response
          settings page. Most properties never need to change the defaults. See the{' '}
          <strong className="text-foreground">Lead Time Amplification</strong> guide for details.
        </p>
      </GuideSection>

      <GuideSection title="Rate Stability">
        <p className="text-sm leading-relaxed text-muted-foreground">
          After the pipeline runs, the engine checks whether the rate changed too sharply from the
          previous computation. This prevents guests and OTAs from seeing erratic price swings.
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Decrease dampened</strong> — if the new rate is
            significantly lower than the previous rate, the decrease is softened. The rate still
            moves down, but gradually rather than in one large drop.
          </li>
          <li>
            <strong className="text-foreground">Increase capped</strong> — if the new rate would jump
            sharply upward, the increase is limited. The rate still rises, but over multiple
            computation cycles rather than all at once.
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Rate stability is automatic and requires no configuration. You can see when it is active
          in the pipeline breakdown — it shows the computed rate alongside the applied rate, so you
          know exactly how much the adjustment was smoothed.
        </p>
      </GuideSection>

      <GuideSection title="How Demand Works With Events">
        <p className="text-sm leading-relaxed text-muted-foreground">
          When you assign an event to your property with a multiplier (e.g., 1.5x), you're making a prediction:
          "I expect demand to be 50% higher than normal for these dates."
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          The demand system measures actual bookings against <strong className="text-foreground">that prediction</strong>, not against
          normal patterns. This prevents double-counting — your event multiplier sets the price target,
          and demand fine-tunes around it.
        </p>

        <div className="my-4 overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/40 bg-slate-50/50">
                <th className="px-4 py-2 text-left text-xs font-semibold text-foreground">Situation</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-foreground">What demand does</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-foreground">Result</th>
              </tr>
            </thead>
            <tbody className="text-xs">
              <tr className="border-b border-border/20">
                <td className="px-4 py-2 text-muted-foreground">Bookings match your prediction</td>
                <td className="px-4 py-2">Near-zero correction</td>
                <td className="px-4 py-2 font-medium">Event price holds as you set it</td>
              </tr>
              <tr className="border-b border-border/20">
                <td className="px-4 py-2 text-muted-foreground">Bookings are slower than predicted</td>
                <td className="px-4 py-2">Small negative correction</td>
                <td className="px-4 py-2 font-medium">Price eases slightly below event target</td>
              </tr>
              <tr>
                <td className="px-4 py-2 text-muted-foreground">Bookings exceed your prediction</td>
                <td className="px-4 py-2">Small positive correction</td>
                <td className="px-4 py-2 font-medium">Price rises slightly above event target</td>
              </tr>
            </tbody>
          </table>
        </div>

        <GuideWidget title="Try it: Event + Demand Scenario">
          <EventDemandScenario />
        </GuideWidget>

        <GuideTip type="do">
          <strong>New properties:</strong> Until {brand} has 2–3 weeks of booking history, event
          dates rely entirely on your multiplier prediction. Demand auto-correction activates once
          enough data is collected.
        </GuideTip>

        <GuideTip type="do">
          If demand consistently corrects your events downward, consider lowering the multiplier.
          Your multiplier is your prediction — demand tells you how accurate it is.
        </GuideTip>
      </GuideSection>

      <GuideSection title="Demand Cap">
        <p className="text-sm leading-relaxed text-muted-foreground">
          The maximum demand adjustment setting is your safety net. It limits how far demand alone
          can move your rate, regardless of how extreme the booking pace or urgency. The default is{' '}
          <strong className="text-foreground">±60%</strong>. Your min/max rate bounds (Price
          Guardrails) provide an additional hard floor and ceiling.
        </p>
      </GuideSection>

      <GuideSection title="Occupancy Curve Fallback">
        <p className="text-sm leading-relaxed text-muted-foreground">
          When baseline data has not built up yet (new properties, new room types), the engine falls back
          to a simpler model. It looks at the{' '}
          <strong className="text-foreground">current occupancy level</strong> and applies a modifier
          from a preset curve aligned with your sensitivity setting.
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>Low occupancy → negative modifier (encourage bookings)</li>
          <li>High occupancy → positive modifier (capture revenue)</li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          This fallback becomes less relevant as your booking history grows and the booking curve takes
          over. You do not need to configure anything -- the transition happens automatically.
        </p>
      </GuideSection>

      <GuideSection title="Choosing Your Sensitivity">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Consider your property type, location, and competitive landscape:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Lots of competition nearby</strong> → Conservative or
            Balanced. You cannot price too far from the market without losing bookings.
          </li>
          <li>
            <strong className="text-foreground">Unique property with few alternatives</strong> → Balanced
            or Aggressive. Guests have limited options, so you can capture more of the demand signal.
          </li>
          <li>
            <strong className="text-foreground">Seasonal property with big demand swings</strong> →
            Aggressive. You need to maximise peak periods because low season revenue is structurally
            limited.
          </li>
          <li>
            <strong className="text-foreground">Trying Aggressive for the first time</strong> → the
            demand cap (default ±60%) provides a safety net. Start there and widen it later if
            you are comfortable with the results.
          </li>
        </ul>
      </GuideSection>

      <GuideSection title="Tips">
        <div className="space-y-3">
          <GuideTip type="do">
            Start with Balanced. The system automatically responds more strongly to short-term
            signals and more gently to long-term ones -- you don't need to configure anything
            for this to work.
          </GuideTip>
          <GuideTip type="do">
            Remember that Conservative is not "safe" -- it means you will capture less revenue during
            surprise demand spikes and be slower to fill during quiet periods.
          </GuideTip>
          <GuideTip type="dont">
            Don't switch to Aggressive on day one before baseline data has built up. The system needs
            history to compare against.
          </GuideTip>
          <GuideTip type="dont">
            Don't fight the demand signal with overrides. If rates are dropping because demand is low,
            that is the system working correctly. Lowering rates to fill rooms often generates more total
            revenue than holding firm and leaving rooms empty.
          </GuideTip>
        </div>
      </GuideSection>

      <GuideRelatedLinks
        links={[
          { slug: 'lead-time', title: 'Lead Time Amplification' },
          { slug: 'events', title: 'Events' },
          { slug: 'base-rates', title: 'Base Rates' },
          { slug: 'occupancy-metrics', title: 'Occupancy Metrics' },
        ]}
      />
    </GuideLayout>
  )
}
